import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material'
import { StyledButton } from '../UI/StyledButton'
import Header from '../UI/Header'
import { useTheme } from '@mui/material/styles'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { searchMonitoring } from '../services/searchMonitoring'
import { getAllElement } from '../services/getAllElement'
import { getListaUtenzeAssegnatariMonitoraggio } from '../services/getListaUtenzeAssegnatariMonitoraggio'
import ResultsTableMonitoring from '../Charts/ResultTableMonitoring'
import DynamicFilterMenu from './components/DynamicFilterMenu'
import dayjs from 'dayjs'
import * as XLSX from 'xlsx'
import { format } from 'date-fns'

const MAX_FILTERS = 11

const Monitoring = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const [searchString, setSearchString] = useState('')
  const [selectedRowData, setSelectedRowData] = useState(undefined)
  const [searchResult, setSearchResult] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [showClosedRequests, setShowClosedRequests] = useState(false)

  const { data: listaPratiche, isLoading, refetch } = useQuery(
    ['searchMonitoring'],
    () => searchMonitoring(''),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 5 * 60 * 1000,
      onSuccess: (data) => {
        setSearchResult(data.data)
      },
    },
  )

  useEffect(() => {
    if (listaPratiche) {
      let filteredResults = listaPratiche.data
      filteredResults = showClosedRequests
        ? filteredResults.filter(pratica => pratica.status === 'CHIUSA')
        : filteredResults.filter(pratica => pratica.status !== 'CHIUSA')
      setSearchResult(filteredResults)
    }
  }, [listaPratiche, showClosedRequests])

  useEffect(() => {
    refetch()
  }, [location, refetch])

  const [anchorEl, setAnchorEl] = useState(null)
  const [open, setOpen] = useState(false)

  const [activeColumns, setActiveColumns] = useState([])
  const [count, setCount] = useState(0)

  const [selectSearchingStato, setSelectSearchingStato] = useState(null)
  const [selectSearchingEsito, setSelectSearchingEsito] = useState(null)
  const [selectSearchingEsitoMonitoraggio, setSelectSearchingEsitoMonitoraggio] = useState(null)
  const [selectSearchingLivelloRischio, setSelectSearchingLivelloRischio] = useState(null)
  const [selectGestore, setSelectGestore] = useState(null)
  const [selectInSofferenza, setSelectInSofferenza] = useState(null)

  const [textSearchingRagSociale, setTextSearchingRagSociale] = useState(null)
  const [textSearchingNdg, setTextSearchingNdg] = useState(null)
  const [textSearchingCf, setTextSearchingCf] = useState(null)
  const [textSearchingGiorni, setTextSearchingGiorni] = useState(null)

  const [dateSearching, setDateSearching] = useState(null)
  const [dateSearchingStatus, setDateSearchingStatus] = useState(null)

  const [filtered, setFiltered] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)

  const [stato, setStato] = useState(null)
  const [esito, setEsito] = useState(null)
  const [esitoM, setEsitoM] = useState(null)
  const [livelloRischio, setLivelloRischio] = useState(null)

  const [dataPraticaDa, setDataPraticaDa] = useState(null)
  const [dataPraticaA, setDataPraticaA] = useState(null)
  const [dataStatusDa, setDataStatusDa] = useState(null)
  const [dataStatusA, setDataStatusA] = useState(null)

  const [cf, setCf] = useState(null)
  const [ndg, setNdg] = useState(null)
  const [ragioneSociale, setRagioneSociale] = useState(null)
  const [gestore, setGestore] = useState(null)
  const [giorni, setGiorni] = useState(null)
  const [inSofferenza, setInSofferenza] = useState(null)

  const [isStatoEnabled] = useState(true)
  const [isDataPraticaEnabled] = useState(true)

  const [statiList, setStatiList] = useState([])
  const [livelloDiRischioList, setLivelloDiRischioList] = useState([])
  const [gestoreList, setGestoreList] = useState([])

  useEffect(() => {
    getAllElement()
      .then((response) => {
        setStatiList(response.data.statiList || [])
        setLivelloDiRischioList(response.data.livelliDiRischioList || [])
      })
      .catch((error) => console.error('Errore nel recupero di stati e livelli di rischio:', error))

    getListaUtenzeAssegnatariMonitoraggio(true)
      .then((lista) => setGestoreList(lista || []))
      .catch((error) => console.error('Errore nel recupero dei gestori:', error))
  }, [])

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }
  const handleCloseMenu = () => {
    setAnchorEl(null)
    setOpen(false)
  }

  const handleDownloadExcel = () => {
    const mappedData = filtered.map((item) => {
      const dataStatus = item.dataStatus
      return {
        NDG: item.ndg,
        codiceFiscale: item.codiceFiscale,
        ragioneSociale: item.ragioneSociale,
        livelloRischio: item.livelloDiRischio,
        dataApertura: new Date(item.dataApertura).toLocaleDateString(),
        esitoSintetico: item.esitoSintetico,
        status: item.status,
        dataStatus: dataStatus ? new Date(item.dataStatus).toLocaleDateString() : '',
        giorni: item.giorniAperturaPratica,
        esitoMonitoraggioFondi: item.esitoMonitoraggioFondi,
        gestore: item.gestore,
        inSofferenza: item.inSofferenza ? 'SI' : 'NO',
      }
    })

    const ws = XLSX.utils.json_to_sheet(mappedData)
    ws['!cols'] = Array(12).fill({ wch: 20 })
    const headerNames = ['NDG', 'Codice Fiscale', 'Ragione Sociale', 'Livello di rischio', 'Data Apertura', 'Presenza variazioni Cerved', 'Status', 'Data Status', 'Giorni apertura pratica', 'Presenza check uso fondi', 'Gestore', 'In sofferenza']
    XLSX.utils.sheet_add_aoa(ws, [headerNames], { origin: 'A1' })

    const formattedDate = format(new Date(), 'ddMMyyyy')
    const fileName = `estrazione_dati_${formattedDate}.xlsx`
    XLSX.writeFile({ Sheets: { 'Dati': ws }, SheetNames: ['Dati'] }, fileName)
  }

  const handleColumnChange = (rowIndex, value) => {
    setActiveColumns(prev => {
      const next = [...prev]
      next[rowIndex] = value
      return next
    })
  }

  const handleAdd = () => {
    setCount(c => Math.min(c + 1, MAX_FILTERS))
  }

  const handleClearAll = () => {
    setRagioneSociale('')
    setNdg('')
    setGiorni('')
    setCf('')
    setStato(null)
    setGestore(null)
    setEsito(null)
    setEsitoM(null)
    setLivelloRischio(null)
    setInSofferenza(null)
    setDataPraticaDa(null)
    setDataPraticaA(null)
    setDataStatusDa(null)
    setDataStatusA(null)
  }

  const handleRemoveAll = () => {
    setCount(0)
    setActiveColumns([])
    handleClearAll()
  }

  const handleRemove = () => {
    setCount(c => {
      const next = Math.max(0, c - 1)
      if (next === 0) {
        setActiveColumns([])
        handleClearAll()
      } else {
        setActiveColumns(prev => prev.slice(0, next))
      }
      return next
    })
  }

  const handleRemoveAndClearAll = () => {
    handleRemoveAll()
  }

  const resetToInitialList = () => {
    setFiltered([])
    setShowClosedRequests(false)
    setSearchString('')
    setSearchResult(listaPratiche.data)
    setIsSearching(false)
    setIsFiltering(false)
    handleRemoveAll()
  }

  const handleRowClick = (params) => {
    navigate('/dettaglioMonitoraggio', { state: { selectedRowData: params.row } })
  }

  function handleFiltra() {
    let filteredData = listaPratiche.data
    setIsFiltering(true)

    if (ndg) {
      if (textSearchingNdg === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.ndg === ndg)
      } else if (textSearchingNdg === 'Contiene') {
        filteredData = filteredData.filter(pratica => (pratica.ndg || '').toLowerCase().includes(ndg.toLowerCase()))
      }
    }

    if (cf) {
      if (textSearchingCf === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.codiceFiscale === cf)
      } else if (textSearchingCf === 'Contiene') {
        filteredData = filteredData.filter(pratica => (pratica.codiceFiscale || '').toLowerCase().includes(cf.toLowerCase()))
      }
    }

    if (ragioneSociale) {
      if (textSearchingRagSociale === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.ragioneSociale === ragioneSociale)
      } else if (textSearchingRagSociale === 'Contiene') {
        filteredData = filteredData.filter(pratica => (pratica.ragioneSociale || '').toLowerCase().includes(ragioneSociale.toLowerCase()))
      }
    }

    if (gestore) {
      if (selectGestore === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.gestore === gestore)
      } else if (selectGestore === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.gestore !== gestore)
      }
    }

    if (dataPraticaDa) {
      const dataPraticaDaInizioGiorno = dayjs(dataPraticaDa).startOf('day').toDate()

      if (dateSearching === 'Maggiore') {
        filteredData = filteredData.filter(pratica => new Date(pratica.dataApertura) >= dataPraticaDaInizioGiorno)
      } else if (dateSearching === 'Minore') {
        filteredData = filteredData.filter(pratica => new Date(pratica.dataApertura) <= dataPraticaDaInizioGiorno)
      } else if (dateSearching === 'Uguale') {
        filteredData = filteredData.filter(pratica => {
          const createdAtDate = new Date(pratica.dataApertura)
          createdAtDate.setHours(0, 0, 0, 0)
          const bound = new Date(dataPraticaDaInizioGiorno)
          bound.setHours(0, 0, 0, 0)
          return createdAtDate.getTime() === bound.getTime()
        })
      } else if (dateSearching === 'Periodo' && dataPraticaA) {
        const da = dayjs(dataPraticaDa).startOf('day').toDate()
        const a = dayjs(dataPraticaA).startOf('day').toDate()
        filteredData = filteredData.filter(pratica => {
          const createdAtDate = new Date(pratica.dataApertura)
          return createdAtDate >= da && createdAtDate <= a
        })
      }
    }

    if (stato) {
      if (selectSearchingStato === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.status === stato)
      } else if (selectSearchingStato === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.status !== stato)
      }
    }

    if (dataStatusDa) {
      const dataStatusDaInizioGiorno = dayjs(dataStatusDa).startOf('day')

      if (dateSearchingStatus === 'Maggiore') {
        filteredData = filteredData.filter(pratica => new Date(pratica.dataStatus) >= dataStatusDaInizioGiorno)
      } else if (dateSearchingStatus === 'Minore') {
        filteredData = filteredData.filter(pratica => new Date(pratica.dataStatus) <= dataStatusDaInizioGiorno)
      } else if (dateSearchingStatus === 'Uguale') {
        filteredData = filteredData.filter(pratica => {
          const createdAtDate = new Date(pratica.dataStatus)
          createdAtDate.setHours(0, 0, 0, 0)
          const bound = new Date(dataStatusDaInizioGiorno)
          bound.setHours(0, 0, 0, 0)
          return createdAtDate.getTime() === bound.getTime()
        })
      } else if (dateSearchingStatus === 'Periodo' && dataStatusA) {
        const da = dayjs(dataStatusDa).startOf('day')
        const a = dayjs(dataStatusA).startOf('day')
        filteredData = filteredData.filter(pratica => {
          const createdAtDate = new Date(pratica.dataStatus)
          return createdAtDate >= da && createdAtDate <= a
        })
      }
    }

    if (esito) {
      if (selectSearchingEsito === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.esitoSintetico === esito)
      } else if (selectSearchingEsito === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.esitoSintetico !== esito)
      }
    }

    if (esitoM) {
      if (selectSearchingEsitoMonitoraggio === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.esitoMonitoraggioFondi === esitoM)
      } else if (selectSearchingEsitoMonitoraggio === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.esitoMonitoraggioFondi !== esitoM)
      }
    }

    if (inSofferenza) {
      const wantTrue = inSofferenza === 'SI'
      if (selectInSofferenza === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.inSofferenza === wantTrue)
      } else if (selectInSofferenza === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.inSofferenza !== wantTrue)
      }
    }

    if (livelloRischio) {
      if (selectSearchingLivelloRischio === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.livelloDiRischio === livelloRischio)
      } else if (selectSearchingLivelloRischio === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.livelloDiRischio !== livelloRischio)
      }
    }

    if (giorni !== null && giorni !== '') {
      const giorniInt = parseInt(giorni, 10)
      if (textSearchingGiorni === 'Uguale') {
        filteredData = filteredData.filter(pratica => pratica.giorniAperturaPratica === giorniInt)
      } else if (textSearchingGiorni === 'Diverso') {
        filteredData = filteredData.filter(pratica => pratica.giorniAperturaPratica !== giorniInt)
      }
    }

    setFiltered(filteredData)
  }

  const handleSearch = async () => {
    if (!searchString.trim()) {
      setSearchResult(listaPratiche)
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    try {
      const response = await searchMonitoring(searchString)
      if (response.data) {
        let filteredResults = response.data
        if (!showClosedRequests) {
          filteredResults = filteredResults.filter(pratica => pratica.status !== 'CHIUSA')
        }
        setSearchResult(filteredResults)
      } else {
        setSearchResult([])
      }
    } catch (error) {
      console.error('Errore nella ricerca:', error)
      setSearchResult([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleInputChange = (event) => setSearchString(event.target.value)

  const handleSwitchChange = (event) => {
    setShowClosedRequests(event.target.checked)
    filterResults(event.target.checked, searchString)
  }

  const filterResults = (showClosed, search) => {
    let filteredResults = listaPratiche.data
    if (showClosed) {
      filteredResults = filteredResults.filter(pratica => pratica.status === 'CHIUSA')
    }
    if (search) {
      filteredResults = filteredResults.filter(pratica =>
        pratica.ragioneSociale.toLowerCase().includes(search.toLowerCase()) &&
        pratica.status !== 'CHIUSA',
      )
    }
    setSearchResult(filteredResults)
  }

  useEffect(() => {
    if (selectedRowData !== undefined) {
      navigate('/dettaglioMonitoraggio', { state: { selectedRowData } })
    }
  }, [selectedRowData, navigate])

  const fields = [
    {
      label: 'NDG',
      type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingNdg,
      onSearchTypeChange: (e) => setTextSearchingNdg(e.target.value),
      value: ndg,
      onChange: (e) => setNdg(e.target.value),
      onClear: () => setNdg(''),
    },
    {
      label: 'Codice Fiscale',
      type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingCf,
      onSearchTypeChange: (e) => setTextSearchingCf(e.target.value),
      value: cf,
      onChange: (e) => setCf(e.target.value),
      onClear: () => setCf(''),
    },
    {
      label: 'Ragione Sociale',
      type: 'text',
      searchTypeOptions: ['Uguale', 'Contiene'],
      searchType: textSearchingRagSociale,
      onSearchTypeChange: (e) => setTextSearchingRagSociale(e.target.value),
      value: ragioneSociale,
      onChange: (e) => setRagioneSociale(e.target.value),
      onClear: () => setRagioneSociale(''),
    },
    {
      label: 'Gestore',
      type: 'select',
      options: gestoreList,
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: selectGestore,
      onSearchTypeChange: (e) => setSelectGestore(e.target.value),
      value: gestore,
      onChange: (e) => setGestore(e.target.value),
      onClear: () => setGestore(null),
    },
    {
      label: 'Data Apertura',
      type: 'dateRange',
      dateSearchTypeOptions: ['Uguale', 'Maggiore', 'Minore', 'Periodo'],
      dateSearchType: dateSearching,
      onDateSearchTypeChange: (e) => setDateSearching(e.target.value),
      valueDa: dataPraticaDa,
      onChangeDa: (date) => setDataPraticaDa(date),
      onClearDa: () => setDataPraticaDa(null),
      valueA: dataPraticaA,
      onChangeA: (date) => setDataPraticaA(date),
      onClearA: () => setDataPraticaA(null),
      disabled: !isDataPraticaEnabled,
    },
    {
      label: 'Data Status',
      type: 'dateRange',
      dateSearchTypeOptions: ['Uguale', 'Maggiore', 'Minore', 'Periodo'],
      dateSearchType: dateSearchingStatus,
      onDateSearchTypeChange: (e) => setDateSearchingStatus(e.target.value),
      valueDa: dataStatusDa,
      onChangeDa: (date) => setDataStatusDa(date),
      onClearDa: () => setDataStatusDa(null),
      valueA: dataStatusA,
      onChangeA: (date) => setDataStatusA(date),
      onClearA: () => setDataStatusA(null),
    },
    {
      label: 'Status',
      type: 'select',
      options: statiList,
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: selectSearchingStato,
      onSearchTypeChange: (e) => setSelectSearchingStato(e.target.value),
      value: stato,
      onChange: (e) => setStato(e.target.value),
      onClear: () => setStato(null),
      disabled: !isStatoEnabled,
    },
    {
      label: 'Presenza variazioni Cerved',
      type: 'select',
      options: ['SI', 'NO'],
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: selectSearchingEsito,
      onSearchTypeChange: (e) => setSelectSearchingEsito(e.target.value),
      value: esito,
      onChange: (e) => setEsito(e.target.value),
      onClear: () => setEsito(null),
    },
    {
      label: 'Giorni apertura pratica',
      type: 'text',
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: textSearchingGiorni,
      onSearchTypeChange: (e) => setTextSearchingGiorni(e.target.value),
      value: giorni,
      onChange: (e) => setGiorni(e.target.value),
      onClear: () => setGiorni(''),
    },
    {
      label: 'Presenza check uso fondi',
      type: 'select',
      options: ['SI', 'NO'],
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: selectSearchingEsitoMonitoraggio,
      onSearchTypeChange: (e) => setSelectSearchingEsitoMonitoraggio(e.target.value),
      value: esitoM,
      onChange: (e) => setEsitoM(e.target.value),
      onClear: () => setEsitoM(null),
    },
    {
      label: 'Livello di rischio',
      type: 'select',
      options: livelloDiRischioList,
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: selectSearchingLivelloRischio,
      onSearchTypeChange: (e) => setSelectSearchingLivelloRischio(e.target.value),
      value: livelloRischio,
      onChange: (e) => setLivelloRischio(e.target.value),
      onClear: () => setLivelloRischio(null),
    },
    {
      label: 'In sofferenza',
      type: 'select',
      options: ['SI', 'NO'],
      searchTypeOptions: ['Uguale', 'Diverso'],
      searchType: selectInSofferenza,
      onSearchTypeChange: (e) => setSelectInSofferenza(e.target.value),
      value: inSofferenza,
      onChange: (e) => setInSofferenza(e.target.value),
      onClear: () => setInSofferenza(null),
    },
  ]

  return (
    <div>
      <Header
        showHome={true}
        showRichiesta={false}
        showStatistiche={false}
        showAccountingMonitoraggio={true}
        showSoggettiTerzi={true}
        header={
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            <Grid item xs={12} sx={{ textAlign: 'center', height: '40px', marginTop: '1rem' }}>
              <Typography sx={{ flexGrow: 1 }} mb={'1rem'} color={theme.palette.black.main} variant="h1">
                MONITORAGGIO
              </Typography>
            </Grid>
            <Grid item>
              <TextField
                margin="normal"
                placeholder="Cerca"
                required
                value={searchString}
                variant="standard"
                id="search"
                name="search"
                autoComplete="search"
                onChange={handleInputChange}
                autoFocus
                style={{ flexGrow: 1, marginRight: '10px' }}
              />
              <FormControlLabel
                control={<Switch checked={showClosedRequests} onChange={handleSwitchChange} size="small" />}
                label={<Typography sx={{ color: theme.palette.black.main }} variant="caption">Richieste Chiuse</Typography>}
              />
              <StyledButton
                onClick={handleSearch}
                sx={{ backgroundColor: theme.palette.grey.light, color: theme.palette.black.main, border: '0px', paddingRight: '10px', mr: 4, mt: 2 }}
                disabled={!searchString.trim()}
              >
                <Typography variant="caption">CERCA</Typography>
              </StyledButton>
              <StyledButton
                sx={{ backgroundColor: theme.palette.grey.light, color: theme.palette.black.main, border: '0px', paddingRight: '10px', mr: 4, mt: 2 }}
                onClick={() => resetToInitialList()}
              >
                <Typography variant="caption">ANNULLA</Typography>
              </StyledButton>
            </Grid>
          </Grid>
        }
      />
      {isLoading && !isSearching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={0} columns={{ xs: 4, sm: 8, md: 12 }} justifyItems="space-between"
          sx={{ marginLeft: '2vw', marginRight: '2vw', maxWidth: '96vw' }}>
          <Grid item xs={12}>
            <StyledButton
              onClick={handleOpenMenu}
              sx={{ backgroundColor: theme.palette.grey.light, color: theme.palette.black.main, border: '0px', paddingRight: '10px', ml: 0, mt: 0 }}
            >
              <Typography variant="caption">FILTRI</Typography>
            </StyledButton>

            <DynamicFilterMenu
              anchorEl={anchorEl}
              open={open}
              handleClose={handleCloseMenu}
              handleAdd={handleAdd}
              handleRemove={handleRemove}
              handleFiltra={handleFiltra}
              handleDownloadExcel={handleDownloadExcel}
              resetToInitialList={resetToInitialList}
              isFiltering={isFiltering}
              fields={fields}
              activeColumns={activeColumns}
              onColumnChange={handleColumnChange}
              count={count}
              maxFilters={MAX_FILTERS}
            />

            <Box sx={{ padding: 0, width: '100%', height: { xs: '500px', xl: '650px' } }}>
              <ResultsTableMonitoring
                listaPratiche={isFiltering ? filtered : searchResult}
                isFetchingListaPratiche={isLoading && !isSearching}
                onRowClick={handleRowClick}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

export default Monitoring
